import { reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useLoader } from '../useLoader'
import { useError } from '../useError'
import { useReceipts } from './useReceipts'
import { useNotifications } from '@/services/notification/useNotifications'
import { useDialog } from '@/services/useDialog'
import { useLogs } from '@/services/useLogs'
import { useTopData } from '@/services/useHome'
import axios from '@/axios'

const rejectHeaders = reactive([
  { title: '経費項目', attr: 'expenseTypeId', comment: '' },
  { title: '支出額', attr: 'expenseAmount', comment: '' },
  { title: '支出年月日', attr: 'expenseDate', comment: '' },
  { title: '支出内容', attr: 'expenseContent1', comment: '' },
  { title: '補記', attr: 'expenseContent2', comment: '' },
  { title: '債権者住所・氏名', attr: 'creditorInfo', comment: '' },
  { title: '領収書添付', attr: 'files', comment: '' },
  { title: '資料添付', attr: 'additionalResources', comment: '' },
  { title: 'メッセージ', attr: 'officeMessage', comment: '' },
])

const isRejectBtnEnabled = ref(false)
const rejectedAttrs = reactive([])

export const useReceiptReject = () => {
  const { currentReceipt, getReceiptsAfterResponse } = useReceipts()
  const { getNotificationsAfterResponse } = useNotifications()
  const { getTimelineAfterResponse } = useLogs()
  const { getTopDataAfterResponse } = useTopData()
  const { toggleShowLoader } = useLoader()
  const { toggleShowError } = useError()
  const route = useRoute()
  const resetRejectedItems = () => {
    while (rejectedAttrs.length > 0) {
      rejectedAttrs.pop()
    }
    rejectHeaders.forEach(x => (x.comment = ''))
  }

  let data = reactive({})

  const toUnderscoreCase = str => {
    return str
      .split(/(?=[A-Z])/)
      .join('_')
      .toLowerCase()
  }

  const toSnake = () => {
    for (let i = 0; i < rejectedAttrs.length; i++) {
      Object.keys(rejectedAttrs[i]).forEach(function(key) {
        if (key === 'target') {
          rejectedAttrs[i][key] = toUnderscoreCase(rejectedAttrs[i][key])
        }
      })
    }
  }
  const submitReject = async (id, userRank) => {
    try {
      toggleShowLoader(true)

      if (userRank === 2) {
        // fileの送信を行うためformDataオブジェクトを使用する
        const formdata = new FormData()

        // 保存タイプのフラグのフォームデータを追加
        formdata.append('is_temporary', false)

        // 経費項目のフォームデータを追加
        if (currentReceipt.expenseTypeId) {
          formdata.append(
            'expense_type_id',
            currentReceipt.expenseTypeId[0]?.id,
          )
        }

        // 支出日のフォームデータを追加
        if (currentReceipt.expenseDate) {
          formdata.append('expense_date', currentReceipt.expenseDate)
        }

        // 支出額のフォームデータを追加
        if (currentReceipt.expenseAmount) {
          formdata.append('expense_amount', currentReceipt.expenseAmount)
        }

        // 支払い証明書フラグのフォームデータを追加(入力時のチェックボックスのboolとhaveReceiptImagesのboolの判定が逆のためhaveReceiptImagesのboolを反転させる)
        formdata.append(
          'have_receipt_images',
          !currentReceipt.haveReceiptImages,
        )

        // 支出内容のフォームデータを追加
        if (currentReceipt.expenseContent1) {
          formdata.append('expense_content1', currentReceipt.expenseContent1)
        }

        // 補記のフォームデータを追加
        if (currentReceipt.expenseContent2) {
          formdata.append('expense_content2', currentReceipt.expenseContent2)
        }

        // 債権者住所、氏名のフォームデータを追加
        if (currentReceipt.haveReceiptImages && currentReceipt.creditorInfo) {
          formdata.append('creditor_info', currentReceipt.creditorInfo)
        }

        // 一部充当フラグのフォームデータを追加
        if (currentReceipt.isPartial) {
          formdata.append('is_partial', currentReceipt.isPartial)
        }

        // 一部充当額のフォームデータを追加
        if (currentReceipt.partialAmount && currentReceipt.isPartial === true) {
          formdata.append('partial_amount', currentReceipt.partialAmount)
        }

        // 一部充当内容のフォームデータを追加
        if (
          currentReceipt.partialComment &&
          currentReceipt.isPartial === true
        ) {
          formdata.append('partial_comment', currentReceipt.partialComment)
        }

        // 按分フラグのフォームデータを追加
        formdata.append('is_distributed', currentReceipt.isDistributed)

        // 分子のフォームデータを追加
        if (currentReceipt.numerator && currentReceipt.isDistributed === true)
          formdata.append('numerator', currentReceipt.numerator)

        // 分母のフォームデータを追加
        if (currentReceipt.denominator && currentReceipt.isDistributed === true)
          formdata.append('denominator', currentReceipt.denominator)

        // 添付資料のフォームデータを追加
        currentReceipt.additionalResources.forEach(file => {
          if (file?.display) {
            formdata.append('keep_file_names[]', file.display)
          } else {
            formdata.append('additional_resources[]', file)
          }
        })

        // 画像のフォームデータを追加
        if (currentReceipt.files.length !== 0) {
          for (let i = 0; i < currentReceipt.files.length; i++) {
            Object.keys(currentReceipt.files[i]).forEach(key => {
              if (currentReceipt.files[i][key]) {
                const keyName = 'images[' + i + '][' + key + ']'
                formdata.append(keyName, currentReceipt.files[i][key])
              }
            })
          }
        } else {
          for (let i = 0; i < currentReceipt.images.length; i++) {
            Object.keys(currentReceipt.images[i]).forEach(key => {
              if (currentReceipt.images[i][key]) {
                const keyName = 'images[' + i + '][' + key + ']'
                formdata.append(keyName, currentReceipt.images[i][key])
              }
            })
          }
        }
        let config = {
          headers: {
            'content-type': 'multipart/form-data',
            'X-HTTP-Method-Override': 'PUT',
          },
        }
        await axios.post(`/receipts/${id}`, formdata, config)
      }

      toSnake()
      data = {
        sent_back_items: rejectedAttrs,
      }
      let res = null
      res = await axios.post(`/receipts/reject/${id}`, data)
      if (route.name === 'Receipts') {
        await getReceiptsAfterResponse(res)
      } else if (route.name !== 'Receipts') {
        const { closeDialog } = useDialog()
        closeDialog()
        if (route.name === 'Top') {
          await getTopDataAfterResponse(res)
        } else if (route.name === 'Logs') {
          await getTimelineAfterResponse(res)
        } else if (route.name === 'Notifications') {
          await getNotificationsAfterResponse(res)
        }
      }
      toggleShowLoader(false)
    } catch (e) {
      toggleShowError(true)
      toggleShowLoader(false)
    }
  }

  return {
    rejectHeaders,
    rejectedAttrs,
    isRejectBtnEnabled,
    resetRejectedItems,
    submitReject,
    toSnake,
  }
}
